import React from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Typography } from '../../../display/Typography';
import { MainButton } from '../../../inputs/buttons/MainButton';

// Icons
import AlertIcon from '../../../../assets/icons/alert-icon.svg';

// Styled
import { Container, IconImg, TextGroup, OptionChip } from './styles';

// Types
import { BackgroundLocationRequiredModalProps } from './types';

/**
 * Modal bloqueante que exige el permiso de ubicación en SEGUNDO PLANO.
 *
 * Hermano de `GpsRequiredModal`, y deliberadamente idéntico en forma: las dos
 * son la misma situación —falta un permiso y no se puede seguir grabando en
 * condiciones— así que se ven igual.
 *
 * ─── Cuándo aparece ────────────────────────────────────────────────────────
 *
 * Solo después de que al operario se le HAYA preguntado por la ubicación y la
 * haya concedido. Ver `useBackgroundLocationGate`: antes salía nada más abrir
 * la app en una instalación limpia, exigiendo un permiso que la app todavía no
 * había pedido.
 *
 * ─── Por qué bloquea ───────────────────────────────────────────────────────
 *
 * Sin este permiso el recorrido se graba igual, pero **no sobrevive a que el
 * sistema detenga la app**: ni el watchdog puede recrear el servicio ni se
 * reanuda tras reiniciar la tablet. Y el fallo es silencioso — la traza se
 * corta y nadie se entera hasta revisarla, cuando ya no hay forma de recuperar
 * el trozo perdido.
 *
 * Por eso no hay X ni Cancelar, igual que en `GPS_REQUIRED`. Bloquear aquí es
 * honesto porque el botón «Abrir ajustes» SIEMPRE funciona: hay salida real.
 *
 * ─── Por qué manda a Ajustes y no pide el permiso ──────────────────────────
 *
 * Desde Android 11 el diálogo del sistema ya no ofrece la opción de permitir
 * siempre. La documentación obliga además a enseñar la etiqueta EXACTA del
 * ajuste, que llega en `optionLabel` desde
 * `getBackgroundPermissionOptionLabel()`.
 */
export const BackgroundLocationRequiredModal = ({
  data,
}: BackgroundLocationRequiredModalProps): JSX.Element => {
  const { t } = useTranslation(['modals']);

  return (
    <Container>
      <IconImg src={AlertIcon} alt="" />
      <TextGroup>
        <Typography size="L" variant="semiBold">
          {t('modals:backgroundLocation.title')}
        </Typography>
        <Typography size="M">
          {t('modals:backgroundLocation.message', { option: data.optionLabel })}
        </Typography>
      </TextGroup>
      {data.optionLabel && <OptionChip>{data.optionLabel}</OptionChip>}
      <MainButton
        text={t('modals:backgroundLocation.openSettings')}
        onClick={data.onOpenSettings}
      />
    </Container>
  );
};

export default BackgroundLocationRequiredModal;
